<script setup lang="ts">
import { computed } from 'vue'
import type { Segment } from './types'

/**
 * One horizontal bar split into proportional segments.
 *
 * Purely the picture: every value it shows is also written out in text by the
 * caller's legend, which is what screen readers get (the bar itself is hidden from
 * them) and what keeps the lighter series legible. Segments are separated by a
 * surface-coloured gap rather than outlined.
 */
const props = defineProps<{ segments: readonly Segment[] }>()

const visible = computed(() => props.segments.filter((segment) => segment.value > 0))
</script>

<template>
  <div class="bar" aria-hidden="true">
    <span
      v-for="segment in visible"
      :key="segment.key"
      class="bar__segment"
      :class="{ 'bar__segment--empty': segment.color === 'none' }"
      :style="{
        flexGrow: segment.value,
        background: segment.color === 'none' ? undefined : `var(${segment.color})`,
      }"
      :title="segment.title"
    />
  </div>
</template>

<style scoped>
.bar {
  display: flex;
  gap: 2px;
  height: 0.875rem;
  border-radius: var(--radius-sm);
  overflow: hidden;
}

.bar__segment {
  flex: 0 1 0;
  /* A sliver still shows, so a small reservation is never invisible. */
  min-width: 2px;
}

.bar__segment--empty {
  background: var(--color-surface-sunken);
}
</style>
